import { Button, Group, List, Paper, Stack, Text } from '@mantine/core';
import {
  AlertCircle,
  Award,
  CheckCircle,
  Clock,
  Heart,
  Mic,
  Volume2,
} from 'react-feather';
import { useNavigate } from 'react-router-dom';
import type {
  PeerLeaderBlock,
  VolunteerResponse,
} from '../../../services/api/preSessionReflection.js';
import { BannerCallout } from './BannerCallout';
import { BANNER_GREEN, BANNER_AMBER } from './bannerTones';

/**
 * Peer-leader banner for the participant pre-session page (MED-813 / E4.1).
 *
 * Renders one of six states that reflect the participant's role for the
 * upcoming peer-led session. The parent gates rendering — this component
 * assumes `block` is non-null. All copy is first-draft placeholder per the AC
 * and SUPERSEDES the design mocks where they conflict:
 *   - NO "Message Cohort Manager" CTA (out of scope)
 *   - NO CM contact link — replaced with static prep-guide reference text
 *   - NO "N members are viewing" presence indicator (Phase 1 out of scope)
 *
 * "Start/Open Session Prep" targets E4.2 / MED-814 (`/participant/sessions/:id/prep`),
 * which 404s until that story ships — the URL contract is intentional.
 */

// Shared callout tones (STYLE_GUIDE.md): medleyGreen #4A7C59, warningAmber #E9A23B.
const GREEN = BANNER_GREEN;
const AMBER = BANNER_AMBER;

interface PeerLeaderBannerProps {
  block: PeerLeaderBlock;
  sessionId: string;
  onVolunteer: (response: VolunteerResponse) => void;
  isSubmitting?: boolean;
  /**
   * MED-1168 — invoked when the assigned-but-declined leader (State 7) clicks
   * "Yes, I'll lead". Re-confirms their own RSVP in place, then the page
   * refetches and redirects them to prep. Only rendered for `leader_declined_self`.
   */
  onReconfirmLead?: () => void;
  isReconfirming?: boolean;
}

export function PeerLeaderBanner({
  block,
  sessionId,
  onVolunteer,
  isSubmitting = false,
  onReconfirmLead,
  isReconfirming = false,
}: PeerLeaderBannerProps) {
  const navigate = useNavigate();
  const prepPath = `/participant/sessions/${sessionId}/prep`;
  const leaderName = block.leader_name ?? 'Your leader';
  const cmName = block.cm_name ?? 'Your cohort manager';
  const timeLabel = block.session_time_label ?? 'the scheduled time';
  const firstName = block.participant_first_name || 'there';
  const sessionLabel =
    block.session_number != null
      ? `Session ${block.session_number}`
      : 'the session';

  switch (block.state) {
    // ----- State 1 — Confirmed Leader: "You're leading" -----
    case 'confirmed_leader':
      return (
        <BannerCallout
          tone={GREEN}
          icon={Mic}
          title={`You're leading ${sessionLabel}`}
          subtitle="Assigned via cohort rotation at program start"
        >
          {block.about_this_session && (
            <Paper withBorder radius="sm" p="sm" bg="white">
              <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={4}>
                About This Session
              </Text>
              <Text size="sm">{block.about_this_session}</Text>
            </Paper>
          )}
          <Stack gap={4}>
            <Text size="sm" fw={600}>
              Your next steps
            </Text>
            <List size="sm" spacing={2}>
              <List.Item>
                Complete your pre-session reflection (below)
              </List.Item>
              <List.Item>
                Review your facilitation guide and session materials
              </List.Item>
              <List.Item>{`Lead ${sessionLabel} at ${timeLabel}`}</List.Item>
            </List>
          </Stack>
          <Group justify="space-between" align="center" wrap="wrap" gap="sm">
            <Button color={GREEN.color} onClick={() => navigate(prepPath)}>
              Start Session Prep
            </Button>
            <Text size="xs" c="dimmed">
              Have questions about leading? Your session prep guide has
              everything you need.
            </Text>
          </Group>
        </BannerCallout>
      );

    // ----- State 2 — Backup Volunteer Needed: "Can you lead today?" -----
    case 'backup_needed':
      return (
        <BannerCallout
          tone={AMBER}
          icon={Volume2}
          title={`Can you lead ${sessionLabel} today?`}
          subtitle="Let your cohort manager know if you'd like to take the role · No pressure if you can't"
        >
          <Text size="sm">
            <Text span fw={600}>
              What happened:
            </Text>{' '}
            {`${leaderName} has a conflict and will be unable to lead today. If anyone in the group can step in, the session can still happen at ${timeLabel} as planned.`}
          </Text>
          <List
            size="sm"
            spacing={2}
            icon={
              <CheckCircle
                size={14}
                color="var(--mantine-color-medleyGreen-6)"
              />
            }
          >
            <List.Item>
              Medley&apos;s facilitation guide, agenda, and materials are ready
              for you. You won&apos;t start from scratch.
            </List.Item>
            {block.ai_assist_bullet && (
              <List.Item>
                AI assistance during the session — same support every leader
                gets.
              </List.Item>
            )}
            <List.Item>
              Same group, same topic. You already know everyone.
            </List.Item>
          </List>
          <Group gap="sm">
            <Button
              color={GREEN.color}
              loading={isSubmitting}
              onClick={() => onVolunteer('volunteered')}
            >
              Yes, I&apos;ll lead
            </Button>
            <Button
              variant="default"
              disabled={isSubmitting}
              onClick={() => onVolunteer('declined')}
            >
              I can&apos;t lead today
            </Button>
          </Group>
          <Text size="xs" c="dimmed">
            {`If the session isn't claimed by ${timeLabel}, your Cohort Manager will reach out to reschedule.`}
          </Text>
        </BannerCallout>
      );

    // ----- State 3 — Volunteer Pending CM Confirmation -----
    case 'volunteer_pending':
      return (
        <BannerCallout
          tone={GREEN}
          icon={Clock}
          title={`Thanks for stepping up, ${firstName}`}
          subtitle="Waiting for your cohort manager to confirm your assignment"
        >
          <Text size="sm">
            {`${cmName} has been notified you're willing to lead — you'll hear back shortly.`}
          </Text>
        </BannerCallout>
      );

    // ----- State 4 — Another Participant Assigned: "[Name] stepped in to lead" -----
    case 'another_assigned':
      return (
        <BannerCallout
          tone={GREEN}
          icon={Award}
          title={`${leaderName} stepped in to lead`}
          subtitle={`Session will run at ${timeLabel} as planned`}
        >
          <Text size="sm">
            Good news — the group came through. You&apos;ll attend as a
            participant today.
          </Text>
        </BannerCallout>
      );

    // ----- State 5 — You've Been Assigned as Backup Leader: "You've got this" -----
    case 'backup_assigned':
      return (
        <BannerCallout
          tone={GREEN}
          icon={Heart}
          title={`You've got this, ${firstName}`}
          subtitle={`Role confirmed · The session will run at ${timeLabel} as planned`}
        >
          <Stack gap={4}>
            <Text size="xs" fw={700} c="dimmed" tt="uppercase">
              A couple of quick things
            </Text>
            <List size="sm" spacing={2}>
              <List.Item>
                Open session prep — skim the facilitation guide and session
                materials (~5 min)
              </List.Item>
              <List.Item>{`If possible, settle in a few minutes before the group arrives (${timeLabel})`}</List.Item>
            </List>
          </Stack>
          <Group justify="space-between" align="center" wrap="wrap" gap="sm">
            <Button color={GREEN.color} onClick={() => navigate(prepPath)}>
              Open Session Prep
            </Button>
            <Text size="xs" c="dimmed">
              Questions? Your session prep guide has what you need.
            </Text>
          </Group>
        </BannerCallout>
      );

    // ----- State 7 (MED-1168) — You're still the assigned leader but declined -----
    // A consistent replacement for the old State-4 misclassification: it names no
    // one else, offers a one-click re-confirm that restores the Start Session
    // path in place (you're already the assigned leader → no CM round-trip).
    case 'leader_declined_self':
      return (
        <BannerCallout
          tone={AMBER}
          icon={Mic}
          title={`You're still set to lead ${sessionLabel}`}
          subtitle="You declined earlier — but no one else has taken it, so it's still yours to lead"
        >
          <Text size="sm">
            {`If you're able to lead after all, confirm below and you'll be taken to your session prep to start ${sessionLabel} at ${timeLabel}.`}
          </Text>
          <Group gap="sm">
            <Button
              color={GREEN.color}
              loading={isReconfirming}
              onClick={() => onReconfirmLead?.()}
            >
              Yes, I&apos;ll lead
            </Button>
          </Group>
          <Text size="xs" c="dimmed">
            Still can&apos;t make it? No action needed — your cohort manager is
            working to confirm the session plan.
          </Text>
        </BannerCallout>
      );

    // ----- State 6 — Participant Declined: Session Status Uncertain -----
    case 'declined':
      return (
        <BannerCallout
          tone={AMBER}
          icon={AlertCircle}
          title="Noted — thanks for letting us know"
          subtitle="Your response has been recorded"
        >
          <Text size="sm">
            {`${cmName} is working to confirm the session plan for today. You'll be notified when the session plan is confirmed.`}
          </Text>
        </BannerCallout>
      );

    default:
      return null;
  }
}
